// src/player/domain/services/playerService.ts

import type { Player, PlayerRankings, PlayerSeasons } from "~/src/player/domain/Player";
import type {
  PlayerDTO,
  PlayerRankingsDTO,
  PlayerSeasonsDTO,
} from "~/src/player/infrastrructure/DTO/PlayerDTO";
import { isFutureDate } from "~/src/Shared/utils";
import { getCompetitionIdsToMapInfo } from "~/src/region/datasources/regionsData";
import type { WebContext } from "~/src/Shared/Types";

export const $LEGEND_PLAYER_IDS: number[] = [
  182, 53015, 219, 53003, 234, 53004, 25568, 158, 250, 53018, 53016, 149, 208, 12, 77712,
];

//Ronaldinho, Totti, Pirlo, Hazard, Sheva, Falcao, Joaquin, Kun, Casillas, Nasri, Nainggolan, Saviola, Cissé, Chicharito, Mafeo

interface PlayerService {
  isDraftPlayer: (player: Player) => boolean;
  playerIsVisible: (player: Player | PlayerDTO | PlayerSeasonsDTO) => boolean;
  normalizePlayerDto: (player: PlayerDTO) => Player;
  normalizePlayerSeasonsDto: (player: PlayerSeasonsDTO, webContext: WebContext) => PlayerSeasons;
  normalizePlayerRankingsDto: (player: PlayerRankingsDTO) => PlayerRankings;
}

const playerService = (): PlayerService => ({
  isDraftPlayer: (player: Player): boolean => {
    return player.category !== "draft";
  },

  playerIsVisible: (player: Player): boolean => {
    return player?.metaInformation?.visible === "true" || player?.metaInformation?.visible === undefined;
  },

  normalizePlayerDto(player: PlayerDTO): Player {
    return {
      id: player.id,
      shortName: player.shortName,
      role: player.role,
      gender: player.gender,
      countryId: player.countryId,
      jersey: player.jersey || player.jersey === 0 ? player.jersey.toString() : player.jersey,
      birthDate: player.birthDate,
      height: player.height,
      image: player.image,
      metaInformation: player.metaInformation,
      currentTeam: player.currentTeam || undefined,
      team: player.team || undefined,
      teams: player.teams ? [...player.teams] : undefined,
      category: player.category || undefined,
      turns: player.turns ? [...player.turns] : undefined,
      marketValue: player.marketValue || undefined,
    };
  },

  normalizePlayerSeasonsDto(player: PlayerSeasonsDTO, webContext: WebContext): PlayerSeasons {
    const competitionIds = getCompetitionIdsToMapInfo(webContext);

    return {
      id: player.id,
      shortName: player.shortName,
      role: player.role,
      gender: player.gender,
      countryId: player.countryId,
      country: player.country || undefined,
      jersey: player.jersey || player.jersey === 0 ? player.jersey.toString() : player.jersey,
      birthDate: player.birthDate,
      height: player.height,
      image: player.image,
      metaInformation: player.metaInformation,
      currentTeam: player.currentTeam || undefined,
      team: player.team || undefined,
      teams: player.teams ? [...player.teams] : undefined,
      category: player.category || undefined,
      turns: player.turns ? [...player.turns] : undefined,
      marketValue: player.marketValue,
      seasons: player.seasons.filter(
        s =>
          (s.season.competition.visible &&
            !isFutureDate(s.season.start) &&
            competitionIds.includes(s.season.competitionId)) ||
          s.season.id === 40
      ),
    };
  },

  normalizePlayerRankingsDto(player: PlayerRankingsDTO): PlayerRankings {
    return {
      id: player.id,
      shortName: player.shortName,
      role: player.role,
      gender: player.gender,
      countryId: player.countryId,
      jersey: player.jersey || player.jersey === 0 ? player.jersey.toString() : player.jersey,
      birthDate: player.birthDate,
      height: player.height,
      image: player.image,
      metaInformation: player.metaInformation,
      currentTeam: player.currentTeam || undefined,
      team: player.team || undefined,
      teams: player.teams ? [...player.teams] : undefined,
      category: player.category || undefined,
      turns: player.turns ? [...player.turns] : undefined,
      rankings: player.rankings,
    };
  },
});

export default playerService;
